import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PropagationStatusBadge } from "@/components/propagation/propagation-status-badge";
import { cn } from "@/lib/utils";
import {
  Activity,
  AlertTriangle,
  Database,
  GitCompareArrows,
  Loader2,
  WifiOff,
  XCircle,
} from "lucide-react";
import type {
  IComparisonRetaguarda,
  IStoreComparison,
} from "src/@types/campaign-comparison";
import { ComparisonStatusBadge } from "./comparison-status-badge";
import type { ComparisonRow } from "./columns";
import { ScheduledJobBadge } from "./scheduled-job-badge";
import { fieldLabel, formatFieldValue } from "./field-format";

interface StoreComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  store: ComparisonRow | null;
  retaguarda: IComparisonRetaguarda | null;
  onShowRaw: () => void;
  onGoToSync: (storeCode: number) => void;
}

function InfoItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

const formatDateTime = (value: string | null): string =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "-";

type FieldRow = {
  field: string;
  varValue: string | number | null;
  storeValue: string | number | null;
  isDivergent: boolean;
};

/**
 * Monta a lista COMPLETA de campos (união retaguarda + loja), na ordem dos
 * campos da retaguarda, marcando quais divergem.
 */
function buildFieldRows(
  store: IStoreComparison,
  retaguarda: IComparisonRetaguarda
): FieldRow[] {
  const divergenceByField = new Map(
    store.divergences.map((d) => [d.field, d])
  );
  const keys = [
    ...Object.keys(retaguarda.fields),
    ...Object.keys(store.storeData ?? {}).filter(
      (k) => !(k in retaguarda.fields)
    ),
  ];

  return keys.map((field) => {
    const divergence = divergenceByField.get(field);
    return {
      field,
      // Usa os valores exatos comparados quando há divergência (autoritativo)
      varValue: divergence ? divergence.varValue : retaguarda.fields[field],
      storeValue: divergence
        ? divergence.storeValue
        : store.storeData?.[field] ?? null,
      isDivergent: !!divergence,
    };
  });
}

export function StoreComparisonDialog({
  open,
  onOpenChange,
  store,
  retaguarda,
  onShowRaw,
  onGoToSync,
}: StoreComparisonDialogProps) {
  const isPending = !!store?.__loading;
  const showPropagation = store?.propagationTracked !== false;

  const lastAttempt = store
    ? store.propagation.lastAttemptAt || store.propagation.lastAttempt
    : null;

  const canShowTable =
    !isPending &&
    store &&
    retaguarda &&
    (store.comparisonStatus === "SYNCED" ||
      store.comparisonStatus === "DIVERGENT" ||
      store.comparisonStatus === "NOT_FOUND_IN_STORE");

  const pendingFieldRows: FieldRow[] =
    isPending && retaguarda
      ? Object.entries(retaguarda.fields).map(([field, varValue]) => ({
          field,
          varValue,
          storeValue: null,
          isDivergent: false,
        }))
      : [];

  const fieldRows =
    store && retaguarda && canShowTable
      ? buildFieldRows(store, retaguarda)
      : pendingFieldRows;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <GitCompareArrows className="h-5 w-5 text-primary" />
            Comparativo da loja
          </DialogTitle>
          {store && (
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span>
                Loja #{store.storeCode}
                {store.storeName ? ` - ${store.storeName}` : ""}
              </span>
              {isPending ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Comparando…
                </span>
              ) : (
                <ComparisonStatusBadge status={store.comparisonStatus} />
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        {store && (
          <ScrollArea className="max-h-[64vh] pr-4">
            <div className="space-y-4">
              {/* Aviso: comparação desta loja ainda em andamento */}
              {isPending && (
                <div className="flex items-start gap-2 rounded-md border border-dashed bg-muted/30 p-3 text-sm">
                  <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-muted-foreground" />
                  <div className="text-muted-foreground">
                    A comparação desta loja ainda não está pronta. Exibindo
                    apenas os dados da retaguarda — o resultado da loja aparece
                    assim que o processamento chegar nela.
                  </div>
                </div>
              )}

              {/* Mais informações da loja */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-3">
                <InfoItem
                  label="Cidade / UF"
                  value={
                    store.city || store.uf
                      ? `${store.city ?? "-"}${store.uf ? ` / ${store.uf}` : ""}`
                      : "-"
                  }
                />
                {!isPending && showPropagation && (
                  <>
                    <InfoItem
                      label="Status da propagação"
                      value={
                        <PropagationStatusBadge
                          status={store.propagation.status}
                        />
                      }
                    />
                    <InfoItem
                      label="Tentativas / Retentativas"
                      value={`${store.propagation.attemptCount} / ${store.propagation.retryCount}`}
                    />
                    <InfoItem
                      label="Última tentativa de sync"
                      value={formatDateTime(lastAttempt)}
                    />
                    <InfoItem
                      label="Verificado em"
                      value={formatDateTime(store.checkedAt)}
                    />
                    <InfoItem
                      label="Sincronização agendada"
                      value={
                        store.scheduledJob ? (
                          <ScheduledJobBadge
                            job={store.scheduledJob}
                            withDetails
                          />
                        ) : (
                          <span className="text-muted-foreground">Nenhum</span>
                        )
                      }
                    />
                  </>
                )}
                {store.itemComparison && (
                  <InfoItem
                    label="Itens (Retaguarda vs Loja)"
                    value={
                      <span
                        className={cn(
                          !store.itemComparison.inSync &&
                            "font-semibold text-yellow-700 dark:text-yellow-400"
                        )}
                      >
                        {store.itemComparison.varCount} vs{" "}
                        {store.itemComparison.storeCount}
                      </span>
                    }
                  />
                )}
              </div>

              {/* Ações: comparativo completo + tela de sincronização da loja */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">
                  Comparativo dos campos principais
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onGoToSync(store.storeCode)}
                  >
                    <Activity className="mr-2 h-4 w-4" />
                    Ver sincronização desta loja
                  </Button>
                  {!isPending && (
                    <Button variant="outline" size="sm" onClick={onShowRaw}>
                      <Database className="mr-2 h-4 w-4" />
                      Ver comparativo completo
                    </Button>
                  )}
                </div>
              </div>

              {/* Loja inacessível: sem dados para comparar */}
              {store.comparisonStatus === "UNREACHABLE" && (
                <div className="space-y-2 rounded-md border border-dashed p-4">
                  <div className="flex items-center gap-2 font-medium text-muted-foreground">
                    <WifiOff className="h-4 w-4" />
                    Não foi possível consultar o banco da loja
                  </div>
                  {store.unreachableReason && (
                    <div className="text-sm text-muted-foreground">
                      Motivo: {store.unreachableReason}
                    </div>
                  )}
                </div>
              )}

              {/* Campanha não encontrada na loja */}
              {store.comparisonStatus === "NOT_FOUND_IN_STORE" && (
                <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                  <XCircle className="h-4 w-4" />
                  Campanha não encontrada no banco da loja — a coluna "Loja"
                  aparece vazia.
                </div>
              )}

              {/* Tabela COMPLETA: todos os campos lado a lado */}
              {(canShowTable || isPending) && fieldRows.length > 0 && (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Campo</TableHead>
                        <TableHead>Retaguarda</TableHead>
                        <TableHead>Loja</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fieldRows.map((row) => (
                        <TableRow
                          key={row.field}
                          className={cn(
                            row.isDivergent &&
                              "bg-yellow-50 dark:bg-yellow-950/20"
                          )}
                        >
                          <TableCell className="font-medium">
                            {fieldLabel(row.field)}
                            {row.isDivergent && (
                              <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
                            )}
                          </TableCell>
                          <TableCell>
                            {formatFieldValue(row.field, row.varValue)}
                          </TableCell>
                          <TableCell
                            className={cn(
                              row.isDivergent &&
                                "font-semibold text-yellow-700 dark:text-yellow-400"
                            )}
                          >
                            {isPending ? (
                              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                aguardando…
                              </span>
                            ) : store.comparisonStatus ===
                              "NOT_FOUND_IN_STORE" ? (
                              "—"
                            ) : (
                              formatFieldValue(row.field, row.storeValue)
                            )}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Linha de itens (PRO014) */}
                      {store.itemComparison && (
                        <TableRow
                          className={cn(
                            !store.itemComparison.inSync &&
                              "bg-yellow-50 dark:bg-yellow-950/20"
                          )}
                        >
                          <TableCell className="font-medium">
                            Itens cadastrados
                            {!store.itemComparison.inSync && (
                              <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
                            )}
                          </TableCell>
                          <TableCell>{store.itemComparison.varCount}</TableCell>
                          <TableCell
                            className={cn(
                              !store.itemComparison.inSync &&
                                "font-semibold text-yellow-700 dark:text-yellow-400"
                            )}
                          >
                            {store.itemComparison.storeCount}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Rodapé: última tentativa de sincronização + erro */}
              {!isPending && showPropagation && (
                <div className="text-xs text-muted-foreground">
                  Última tentativa de sincronização:{" "}
                  {formatDateTime(lastAttempt)}
                  {store.propagation.errorMessage && (
                    <> — {store.propagation.errorMessage}</>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
